import { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ShieldAlert, Save, Pencil, X } from "lucide-react";
import { toast } from "sonner";

const SECTIONS = [
  { key: "overview", label: "Overview & Scope", hint: "What this plan covers, objectives, RTO/RPO targets." },
  { key: "key_contacts", label: "Key Contacts", hint: "People to call first — names, roles, phone, email." },
  { key: "critical_systems", label: "Critical Systems & Priorities", hint: "Systems in recovery order, dependencies, priorities." },
  { key: "backup_recovery", label: "Backups & Recovery Sources", hint: "Where backups live, how to access them, credentials location." },
  { key: "recovery_steps", label: "Recovery Steps", hint: "Step-by-step restore procedure for each critical system." },
  { key: "escalation_vendors", label: "Escalation & Vendors", hint: "Vendor/ISP/hardware support contacts and account numbers." },
  { key: "communications", label: "Communications Plan", hint: "Who tells staff/customers what, and how." },
  { key: "notes", label: "Additional Notes", hint: "Anything else useful during an incident." },
];

function emptySections() {
  return SECTIONS.reduce((acc, s) => { acc[s.key] = ""; return acc; }, {});
}

function formatTimestamp(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-NZ", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
}

export default function DRPlan() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const [sections, setSections] = useState(emptySections());
  const [meta, setMeta] = useState({ updated_at: null, updated_by: "" });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(emptySections());

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/dr-plan");
      setSections({ ...emptySections(), ...data.sections });
      setMeta({ updated_at: data.updated_at, updated_by: data.updated_by });
    } catch (e) {
      if (!e?.__silent) toast.error("Failed to load DR plan");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [user?.organization_id]);

  const startEdit = () => { setDraft({ ...sections }); setEditing(true); };
  const cancelEdit = () => { setEditing(false); setDraft({ ...sections }); };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/dr-plan", { sections: draft });
      setSections({ ...emptySections(), ...data.sections });
      setMeta({ updated_at: data.updated_at, updated_by: data.updated_by });
      setEditing(false);
      toast.success("DR plan saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="dr-plan-page">
      <PageHeader
        eyebrow="[13] Continuity"
        title="DR Plan"
        subtitle={`Disaster recovery plan for ${user?.organization_name || "this organization"}. ${isAdmin ? "Admins can edit; everyone can view." : "View only."}`}
        actions={
          isAdmin && !editing ? (
            <Button
              data-testid="dr-edit-btn"
              onClick={startEdit}
              className="rounded-none bg-[#B91C1C] hover:bg-[#a11818] text-white font-mono text-xs tracking-wider uppercase"
            >
              <Pencil className="w-3.5 h-3.5 mr-2" /> Edit Plan
            </Button>
          ) : editing ? (
            <div className="flex items-center gap-2">
              <Button
                data-testid="dr-cancel-btn"
                variant="outline"
                onClick={cancelEdit}
                className="rounded-none border-[#D6CFC1] bg-transparent text-[#3A3A3A] hover:bg-[#F0EDE3] font-mono text-xs tracking-wider uppercase"
              >
                <X className="w-3.5 h-3.5 mr-2" /> Cancel
              </Button>
              <Button
                data-testid="dr-save-btn"
                onClick={save}
                disabled={saving}
                className="rounded-none bg-[#B91C1C] hover:bg-[#a11818] text-white font-mono text-xs tracking-wider uppercase disabled:opacity-50"
              >
                <Save className="w-3.5 h-3.5 mr-2" /> {saving ? "Saving…" : "Save Plan"}
              </Button>
            </div>
          ) : null
        }
      />

      <div className="p-4 sm:p-6 lg:p-8 space-y-5">
        <div className="border-l-2 border-[#B91C1C] bg-[#FEF2F2] px-4 py-3 flex items-center gap-2">
          <ShieldAlert className="w-4 h-4 text-[#B91C1C]" strokeWidth={1.75} />
          <span className="font-mono text-xs text-[#7A1414]">
            Keep this current — it&apos;s what you&apos;ll rely on when systems are down.
          </span>
          <span className="ml-auto font-mono text-[10px] uppercase tracking-wider text-[#9A5555]">
            Updated {formatTimestamp(meta.updated_at)}{meta.updated_by ? ` · ${meta.updated_by}` : ""}
          </span>
        </div>

        {loading ? (
          <div className="font-mono text-xs text-[#7A7A7A] py-10 text-center">Loading…</div>
        ) : (
          <div className="space-y-4">
            {SECTIONS.map((s, i) => (
              <div key={s.key} data-testid={`dr-section-${s.key}`} className="border border-[#E5E0D5] bg-white">
                <div className="px-5 py-3 border-b border-[#E5E0D5] flex items-baseline gap-3">
                  <span className="font-mono text-[10px] text-[#B91C1C]">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="text-[#0F0F0F] font-medium tracking-tight">{s.label}</h3>
                </div>
                <div className="px-5 py-4">
                  {editing ? (
                    <Textarea
                      data-testid={`dr-input-${s.key}`}
                      value={draft[s.key] || ""}
                      onChange={(e) => setDraft({ ...draft, [s.key]: e.target.value })}
                      rows={5}
                      placeholder={s.hint}
                      className="rounded-none bg-white border-[#E5E0D5] font-mono text-sm"
                    />
                  ) : sections[s.key] ? (
                    <p data-testid={`dr-value-${s.key}`} className="text-sm text-[#3A3A3A] whitespace-pre-wrap leading-relaxed">{sections[s.key]}</p>
                  ) : (
                    <p data-testid={`dr-value-${s.key}`} className="font-mono text-xs text-[#9A9A9A] italic">{isAdmin ? "Not filled in yet — click Edit Plan to add." : "Not documented yet."}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
